import React, { useState, useEffect } from 'react';
import TcImage from './TcImage';
import { imageCache } from '../utils/imageCache';

interface TcCrossFadeImageProps {
    imageA: string;
    imageB?: string; // Optional - if not provided, only shows imageA without transitions
    showImageA: boolean;
    alt: string;
    classNameStyle?: string;
    onTransitionComplete?: () => void;
    duration?: number; // Duration in milliseconds (default: 300)
    singleImageMode?: boolean; // If true, disables transitions even when imageB is provided
    preloadImages?: boolean; // If true, preloads images when component mounts (default: true)
}

function TcCrossFadeImage({
    imageA,
    imageB,
    showImageA,
    alt,
    classNameStyle = "mb-6",
    onTransitionComplete,
    duration = 300,
    singleImageMode = false,
    preloadImages = true
}: TcCrossFadeImageProps) {
    const [internalShowImageA, setInternalShowImageA] = useState(showImageA);
    const [imagesLoaded, setImagesLoaded] = useState(false);

    // Preload images when component mounts
    useEffect(() => {
        if (!preloadImages) {
            setImagesLoaded(true);
            return;
        }

        const imagesToPreload = [imageA];
        if (imageB) {
            imagesToPreload.push(imageB);
        }

        // Check if images are already cached
        const allCached = imagesToPreload.every(src => imageCache.isCached(src));
        if (allCached) {
            setImagesLoaded(true);
            return;
        }

        // Preload images that aren't cached
        const uncachedImages = imagesToPreload.filter(src => !imageCache.isCached(src));
        
        imageCache.preloadImages(uncachedImages)
            .then(() => {
                setImagesLoaded(true);
            })
            .catch((error) => {
                console.warn('Failed to preload some images:', error);
                // Still set as loaded to prevent blocking the UI
                setImagesLoaded(true);
            });
    }, [imageA, imageB, preloadImages]);

    React.useEffect(() => {
        // If no imageB provided OR single image mode is enabled, just update internal state immediately (no transition)
        if (!imageB || singleImageMode) {
            setInternalShowImageA(showImageA);
            onTransitionComplete?.();
            return;
        }

        // Only start transition if showImageA actually changed and we have both images and transitions are enabled
        if (showImageA !== internalShowImageA) {
            // Use requestAnimationFrame to ensure smooth transition start
            const rafId = requestAnimationFrame(() => {
                setInternalShowImageA(showImageA);
            });
            
            // Complete transition after duration
            const timer = setTimeout(() => {
                onTransitionComplete?.();
            }, duration); // Use the configurable duration
            
            return () => {
                cancelAnimationFrame(rafId);
                clearTimeout(timer);
            };
        }
    }, [showImageA, internalShowImageA, onTransitionComplete, duration, imageB, singleImageMode]);

    // Determine if transitions should be enabled
    const transitionsEnabled = imageB && !singleImageMode;
    
    return (
        <TcImage classNameStyle={classNameStyle}>
            <div className="relative">
                {/* Loading state - show a placeholder while images are loading */}
                {!imagesLoaded && preloadImages && (
                    <div className="w-full h-64 bg-gray-800 animate-pulse flex items-center justify-center">
                        <div className="text-gray-400 text-sm">Loading images...</div>
                    </div>
                )}
                
                {/* Images - only show when loaded or preloading is disabled */}
                {(imagesLoaded || !preloadImages) && (
                    <>
                        {/* Image A */}
                        <img 
                            src={imageA} 
                            alt={alt} 
                            className={`w-full ${transitionsEnabled ? 'transition-opacity ease-in-out' : ''} ${
                                transitionsEnabled 
                                    ? (internalShowImageA ? 'opacity-100' : 'opacity-0')
                                    : 'opacity-100' // Always visible when transitions disabled
                            }`}
                            style={transitionsEnabled ? { transitionDuration: `${duration}ms` } : {}}
                        />
                        
                        {/* Image B - only render if provided and transitions are enabled */}
                        {transitionsEnabled && (
                            <img 
                                src={imageB} 
                                alt={alt} 
                                className={`w-full absolute top-0 left-0 transition-opacity ease-in-out ${
                                    !internalShowImageA 
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                }`}
                                style={{ transitionDuration: `${duration}ms` }}
                            />
                        )}
                    </>
                )}
            </div>
        </TcImage>
    );
}

export default TcCrossFadeImage; 